import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { Loader } from '../../../inputs/Loader';
import { DateTime } from '../../../inputs/DateTime';
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

import { ModalProps } from './types';
import {
  ActionButton,
  ButtonRow,
  ContainerForm,
  ContainerLoader,
  ContainerText,
  ModalContainerStyled,
  Section,
  Text,
  Title,
} from './styles';

/**
 * ForceCloseRouteModal (rv-recorrido-colgado 2026-07-22).
 *
 * El gestor (Coex/Capataz/SuperAdmin) cierra un recorrido COLGADO (>24h sin
 * actividad, un tramo olvidado sin cerrar) eligiendo la HORA REAL de fin. Esa
 * hora se estampa en el tramo abierto + el RV → el turno se calcula con la
 * ventana correcta (no un span de días) y se corrige el dato. Patrón de modal
 * de confirmación (icono + título centrado + datetime + botones con loading).
 */
export const ForceCloseRouteModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const isLoading = data?.isLoading ?? false;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [endDate, setEndDate] = useState<string | undefined>(
    data?.defaultEndDate
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!data?.onConfirm || isSubmitting || !endDate) return;
      setIsSubmitting(true);
      try {
        await data.onConfirm(endDate);
        onClickClose();
      } catch {
        // El error (toast) ya lo mostró onConfirm; mantenemos el modal abierto
        // para reintentar/cancelar. Sin re-throw → no hay promesa colgada.
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, endDate, isSubmitting, onClickClose]
  );

  return (
    <ModalContainerStyled
      width={55}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      {isSubmitting && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <ContainerForm onSubmit={onSubmit} isLoading={isSubmitting}>
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">
            {t('pages:surveillance.forceClose.title')}
          </Title>
          <Text size="M" colorText="greyDark">
            {data?.roid
              ? t('pages:surveillance.forceClose.subtitle', { roid: data.roid })
              : t('pages:surveillance.forceClose.subtitleNoRoid')}
          </Text>
        </ContainerText>

        <Section>
          <Text size="S">{t('pages:surveillance.forceClose.endDate')}</Text>
          <DateTime
            label={t('pages:surveillance.forceClose.endDate')}
            value={endDate}
            initialDate={endDate}
            toDate={new Date()}
            onChangeDate={(value) => setEndDate(value)}
            data-testid="force-close-datetime"
          />
        </Section>

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={onClickClose}
            disabled={isLoading || isSubmitting}
          />
          <ActionButton
            type="submit"
            text={t('pages:surveillance.forceClose.confirm')}
            disabled={isLoading || isSubmitting || !endDate}
            data-testid="force-close-confirm"
          />
        </ButtonRow>
      </ContainerForm>
    </ModalContainerStyled>
  );
};

export default ForceCloseRouteModal;
